'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

type AnalyticsPreferencesContextValue = {
  preferencesOpen: boolean
  openPreferences: () => void
  closePreferences: () => void
}

const AnalyticsPreferencesContext =
  createContext<AnalyticsPreferencesContextValue | null>(null)

export function AnalyticsPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const openPreferences = useCallback(() => setPreferencesOpen(true), [])
  const closePreferences = useCallback(() => setPreferencesOpen(false), [])
  const value = useMemo(
    () => ({ preferencesOpen, openPreferences, closePreferences }),
    [preferencesOpen, openPreferences, closePreferences],
  )

  return (
    <AnalyticsPreferencesContext.Provider value={value}>
      {children}
    </AnalyticsPreferencesContext.Provider>
  )
}

export function useAnalyticsPreferences(): AnalyticsPreferencesContextValue {
  const context = useContext(AnalyticsPreferencesContext)
  if (!context) {
    throw new Error('Analytics preferences must be rendered inside their provider')
  }
  return context
}
